// ===========================================
// Student Header Component
// ===========================================
// Displays student name, character illustration, contact details, and portal access

import { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  CircularProgress,
  Chip,
} from '@mui/material';
import {
  School as SchoolIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Edit as EditIcon,
  Send as SendIcon,
  Refresh as RefreshIcon,
  CheckCircle as CheckCircleIcon,
  HourglassEmpty as PendingIcon,
  ErrorOutline as ExpiredIcon,
} from '@mui/icons-material';
import { StudentSummary } from '../../api/user.api';
import {
  CharacterIllustration,
  getAgeGroupFromBirthDate,
  getCharacterColor,
  AgeGroup,
} from '../brand';
import { useUpdateChildContact, useMyProfile } from '../../hooks/useParentProfile';
import {
  useStudentInvitationStatus,
  useSendStudentInvitation,
  useResendStudentInvitation,
} from '../../hooks/useInvitations';

// ===========================================
// TYPES
// ===========================================

interface StudentHeaderProps {
  student: StudentSummary;
}

// ===========================================
// COMPONENT
// ===========================================

export default function StudentHeader({ student }: StudentHeaderProps) {
  const [editOpen, setEditOpen] = useState(false);
  const [email, setEmail] = useState(student.email || '');
  const [phone, setPhone] = useState(student.phone || '');
  const [emailError, setEmailError] = useState('');

  const { data: profile } = useMyProfile();
  const updateContact = useUpdateChildContact();
  const isLocked = profile?.contactDetailsLocked ?? false;

  // Portal invitation hooks
  const { data: invitationStatus, isLoading: isLoadingInvitation } = useStudentInvitationStatus(
    student.email ? student.id : null
  );
  const sendInvitation = useSendStudentInvitation();
  const resendInvitation = useResendStudentInvitation();
  const isInvitationPending = sendInvitation.isPending || resendInvitation.isPending;

  // Calculate age group from birth date
  const ageGroup: AgeGroup = student.birthDate
    ? getAgeGroupFromBirthDate(student.birthDate)
    : 'KIDS';

  const characterColor = getCharacterColor(ageGroup);

  const hasContact = student.email || student.phone;

  const handleEditOpen = () => {
    setEmail(student.email || '');
    setPhone(student.phone || '');
    setEmailError('');
    setEditOpen(true);
  };

  const handleSave = () => {
    // Basic email validation
    if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setEmailError('Invalid email address');
      return;
    }
    setEmailError('');

    updateContact.mutate(
      {
        studentId: student.id,
        data: {
          email: email || null,
          phone: phone || null,
        },
      },
      {
        onSuccess: () => setEditOpen(false),
      }
    );
  };

  return (
    <>
      <Paper
        elevation={0}
        sx={{
          p: 2,
          mb: 3,
          bgcolor: `${characterColor}15`, // 15% opacity
          borderLeft: `4px solid ${characterColor}`,
          display: 'flex',
          alignItems: 'center',
          gap: 2,
        }}
      >
        {/* Character Illustration */}
        <Box
          sx={{
            width: 64,
            height: 64,
            borderRadius: '50%',
            bgcolor: 'background.paper',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: 1,
            flexShrink: 0,
          }}
        >
          <CharacterIllustration ageGroup={ageGroup} size="medium" />
        </Box>

        {/* Student Info */}
        <Box sx={{ flex: 1 }}>
          <Typography variant="h5" fontWeight="bold" color="text.primary">
            {student.firstName} {student.lastName}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
            <SchoolIcon fontSize="small" sx={{ color: characterColor }} />
            <Typography variant="body2" color="text.secondary">
              {student.enrollmentCount > 0
                ? `${student.enrollmentCount} ${student.enrollmentCount === 1 ? 'class' : 'classes'} enrolled`
                : 'No active enrollments'}
            </Typography>
            {student.attendanceRate !== null && student.attendanceRate !== undefined && (
              <>
                <Typography variant="body2" color="text.disabled">
                  |
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {Math.round(student.attendanceRate)}% attendance
                </Typography>
              </>
            )}
          </Box>
        </Box>

        {/* Contact Details & Portal Access (right side) */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            flexShrink: 0,
          }}
        >
          {/* Contact Details */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            {hasContact ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, alignItems: 'flex-end' }}>
                {student.email && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <EmailIcon fontSize="small" sx={{ color: 'text.secondary', fontSize: 16 }} />
                    <Typography variant="body2" color="text.secondary">
                      {student.email}
                    </Typography>
                  </Box>
                )}
                {student.phone && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <PhoneIcon fontSize="small" sx={{ color: 'text.secondary', fontSize: 16 }} />
                    <Typography variant="body2" color="text.secondary">
                      {student.phone}
                    </Typography>
                  </Box>
                )}
              </Box>
            ) : (
              <Typography variant="body2" color="text.disabled" sx={{ fontStyle: 'italic' }}>
                No contact details
              </Typography>
            )}

            {!isLocked && (
              <Tooltip title="Edit contact details">
                <IconButton size="small" onClick={handleEditOpen} sx={{ color: 'action.active' }}>
                  <EditIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Box>

          {/* Portal Access - only show when student has email */}
          {student.email && (
            <>
              <Box sx={{ borderLeft: '1px solid', borderColor: 'divider', height: 32 }} />
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {isLoadingInvitation ? (
                  <CircularProgress size={18} />
                ) : invitationStatus?.hasAccount ? (
                  <Chip
                    icon={<CheckCircleIcon fontSize="small" />}
                    label="Portal Active"
                    color="success"
                    size="small"
                  />
                ) : invitationStatus?.status === 'PENDING' ? (
                  <>
                    <Chip
                      icon={<PendingIcon fontSize="small" />}
                      label="Invite Pending"
                      color="warning"
                      size="small"
                    />
                    {invitationStatus.canResend && (
                      <Tooltip title="Resend invitation">
                        <IconButton
                          size="small"
                          onClick={() => resendInvitation.mutate(student.id)}
                          disabled={isInvitationPending}
                        >
                          {isInvitationPending ? <CircularProgress size={16} /> : <RefreshIcon fontSize="small" />}
                        </IconButton>
                      </Tooltip>
                    )}
                  </>
                ) : invitationStatus?.status === 'EXPIRED' ? (
                  <>
                    <Chip
                      icon={<ExpiredIcon fontSize="small" />}
                      label="Invite Expired"
                      color="error"
                      size="small"
                    />
                    <Tooltip title="Resend invitation">
                      <IconButton
                        size="small"
                        onClick={() => resendInvitation.mutate(student.id)}
                        disabled={isInvitationPending}
                      >
                        {isInvitationPending ? <CircularProgress size={16} /> : <RefreshIcon fontSize="small" />}
                      </IconButton>
                    </Tooltip>
                  </>
                ) : invitationStatus?.canInvite ? (
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={isInvitationPending ? <CircularProgress size={14} color="inherit" /> : <SendIcon />}
                    onClick={() => sendInvitation.mutate(student.id)}
                    disabled={isInvitationPending}
                    sx={{ whiteSpace: 'nowrap' }}
                  >
                    Invite to Portal
                  </Button>
                ) : null}
              </Box>
            </>
          )}
        </Box>
      </Paper>

      {/* Edit Contact Dialog */}
      <Dialog open={editOpen} onClose={() => setEditOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>
          Edit Contact Details - {student.firstName}
        </DialogTitle>
        <DialogContent>
          <TextField
            label="Email"
            type="email"
            fullWidth
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              setEmailError('');
            }}
            error={!!emailError}
            helperText={emailError}
            sx={{ mt: 2, mb: 2 }}
            placeholder="student@example.com"
          />
          <TextField
            label="Phone"
            type="tel"
            fullWidth
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder="0400 000 000"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditOpen(false)} disabled={updateContact.isPending}>
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            variant="contained"
            disabled={updateContact.isPending}
            startIcon={updateContact.isPending ? <CircularProgress size={16} /> : undefined}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
